import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { UserService } from '../../../services/user.service';

@Component({
  selector: 'app-privacy-agreement',
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatCheckboxModule,
    FormsModule,
  ],
  templateUrl: './privacy-agreement.html',
  styleUrl: './privacy-agreement.css',
})
export class PrivacyAgreement implements OnInit {
  /** Whether the user has checked the acknowledgment checkbox */
  hasRead = false;

  /** Whether the agreement is currently being saved */
  isSaving = false;

  /** Timestamp of when the user agreed, null if not yet agreed */
  agreedAt: Date | null = null;

  constructor(
    /** Service for accessing and updating the current authenticated user. */
    private userService: UserService,
    /** Used to navigate to the appropriate home page after agreeing. */
    private router: Router,
  ) {}

  /**
   * On init, check if the current user has already agreed.
   * @returns {void}
   */
  ngOnInit(): void {
    const user = this.userService.currentUser;
    if (user?.agreementAgreedAt) {
      this.agreedAt = new Date(user.agreementAgreedAt);
    }
  }

  /**
   * Returns the home route for the current user based on their role.
   * Counselors (role === 'admin') go to /counselors, students go to /students.
   * @returns {string}
   */
  private get homeRoute(): string {
    const roleRoutes: Record<string, string> = {
      admin: '/counselors',
      faculty: '/counselors',
      student: '/students',
      district: '/districts',
    };

    if (this.userService.currentUser) {
      return roleRoutes[this.userService.currentUser.userType] || '/home';
    }
    return '/home';
  }

  /**
   * Handles the user's agreement to the data privacy policy.
   * Disabled if the user has not checked the acknowledgment checkbox.
   * On success, stores the agreement timestamp and routes the user to
   * their respective home page based on role.
   * FERPA §99.7 / COPPA — records user acknowledgment of privacy rights.
   * @returns {void}
   */
  agree(): void {
    if (!this.hasRead || this.isSaving) return;
    this.isSaving = true;

    this.userService.agreeToPrivacyPolicy().subscribe({
      next: (response) => {
        this.agreedAt = new Date(response.agreedAt);
        this.isSaving = false;
        this.router.navigate([this.homeRoute]);
      },
      error: (err) => {
        console.error('Failed to record agreement:', err);
        this.isSaving = false;
      },
    });
  }

  /**
   * Navigates the user back to their respective home page based on role.
   * @returns {void}
   */
  goHome(): void {
    this.router.navigate([this.homeRoute]);
  }
}
